<!--
  商品平台类目编辑/展示组件
  - 用于 editProduct.vue 的“平台类目”区块
  - 接收商品详情 productInfo，自动反显 OTTO/BOL/KAUFLAND 三个平台类目
  - 编辑态使用 PlatformCategorySelect 下拉选择
  - 查看态显示类目路径文本
  - 类目变化时通过 change 事件抛出最新路径
-->
<template>
  <div class="product-platform-category">
    <!-- 标题：与报关信息等区块保持一致 -->
    <div class="productsTit padding-10">
      平台类目
    </div>

    <!-- 内容区：与 form-item-flex-wrap 布局保持一致 -->
    <div class="products-content padding-10 form-item-flex-wrap">
      <!-- 查看模式：横向展示三个平台类目路径 -->
      <template v-if="isDisabled">
        <div v-for="platform in platformList" :key="platform" class="ppc-view-item ivu-form-item">
          <span class="ppc-view-label">{{ platform.toUpperCase() }} 类目：</span>
          <span class="ppc-view-value">{{ getViewLabel(platform) || '未映射' }}</span>
        </div>
      </template>

      <!-- 编辑模式：表单 + 三个下拉选择器 -->
      <Form v-else ref="platformCategoryForm" :model="form" :label-width="120" class="ppc-form" inline>
        <FormItem
          v-for="platform in platformList"
          :key="platform"
          :label="`${platform.toUpperCase()} 类目`"
          :prop="`${platform}.categoryPath`"
        >
          <PlatformCategorySelect
            :ref="`platform-category-select-${platform}`"
            v-model="form[platform].categoryPath"
            :platform="platform"
            :disabled="isDisabled"
            :placeholder="`请选择${platform.toUpperCase()}平台对应类目`"
          />
        </FormItem>
      </Form>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';
import PlatformCategorySelect from '@/components/common/platformCategorySelect';

const PLATFORM_LIST = ['otto', 'bol', 'kaufland'];

// 平台编码映射：组件内使用 kaufland，接口使用 real
const PLATFORM_CODE_MAP = {
  bol: 'bol',
  otto: 'otto',
  kaufland: 'real'
};

export default {
  name: 'ProductPlatformCategory',
  components: { PlatformCategorySelect },
  props: {
    // 商品详情，期望包含 productCategoryId（erp分类id）
    productInfo: { type: Object, default: () => ({}) },
    // 是否禁用（查看模式）
    isDisabled: { type: Boolean, default: false }
  },
  data () {
    return {
      platformList: PLATFORM_LIST,
      form: this.buildEmptyForm(),
      viewLabelMap: {},
      // 平台类目绑定关系主键映射（来自 platformCategoryRel/list），保存时回传 id
      platformRelIdMap: {}
    };
  },
  watch: {
    // 监听商品分类 ID 变化，重新加载该分类下的平台类目绑定关系
    'productInfo.productCategoryId': {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.loadPlatformCategoryRel(newVal);
        } else {
          // 分类为空时清空表单
          this.form = this.buildEmptyForm();
          this.platformRelIdMap = this.getDefaultPlatformRelIdMap();
        }
      }
    },
    // 任一平台类目路径变化时，通知父组件
    form: {
      deep: true,
      handler (val) {
        this.$emit('change', this.getOutputData(val));
      }
    }
  },
  methods: {
    // 构建空表单
    buildEmptyForm () {
      const form = {};
      PLATFORM_LIST.forEach(platform => {
        form[platform] = { categoryPath: [] };
      });
      return form;
    },

    // 平台编码转换：接口返回的 platformId → 组件使用的 platformCode
    getComponentPlatformCode (apiPlatformId) {
      return Object.keys(PLATFORM_CODE_MAP).find(
        key => PLATFORM_CODE_MAP[key] === apiPlatformId
      ) || apiPlatformId;
    },

    // 默认平台绑定关系 id 映射
    getDefaultPlatformRelIdMap () {
      return { bol: null, otto: null, kaufland: null };
    },

    // 查询erp分类绑定平台分类（GET /product-service/platformCategoryRel/list）
    async loadPlatformCategoryRel (productCategoryId) {
      if (!productCategoryId) return;
      try {
        const res = await this.axios.get(api.get_platformCategoryRel, {
          params: { productCategoryId }
        });
        if (res.data.code !== 0) {
          this.$Message.error(res.data.message || '查询平台类目绑定关系失败');
          return;
        }
        const list = res.data.datas || [];
        const idMap = this.getDefaultPlatformRelIdMap();
        PLATFORM_LIST.forEach(platform => {
          this.$set(this.form[platform], 'categoryPath', []);
        });
        for (const item of list) {
          const platformCode = this.getComponentPlatformCode(item.platformId);
          idMap[platformCode] = item.id || null;
          // platformCategoryId 为逗号分隔的多级分类 ID，切割为数组
          const idArr = String(item.platformCategoryId || '').split(',').filter(Boolean);
          if (idArr.length) {
            this.$set(this.form[platformCode], 'categoryPath', idArr);
          }
        }
        this.platformRelIdMap = idMap;
        // 查看模式时预加载路径文本
        if (this.isDisabled) {
          this.loadViewLabels();
        }
      } catch (err) {
        console.error('[loadPlatformCategoryRel] 查询失败', err);
      }
    },

    // 查看模式：获取每个平台的类目路径文本
    getViewLabel (platform) {
      return this.viewLabelMap[platform] || '';
    },

    async loadViewLabels () {
      for (const platform of PLATFORM_LIST) {
        const path = this.form[platform].categoryPath;
        if (!path || !path.length) {
          this.$set(this.viewLabelMap, platform, '');
          continue;
        }
        try {
          const list = await PlatformCategorySelect.getCategoryInfo(platform, path);
          const labels = list.map(item => item.categoryName);
          this.$set(this.viewLabelMap, platform, labels.join(' > '));
        } catch (e) {
          this.$set(this.viewLabelMap, platform, '');
        }
      }
    },

    // 组装对外输出的平台类目数据
    getOutputData (form) {
      const result = {};
      PLATFORM_LIST.forEach(platform => {
        result[platform] = (form[platform].categoryPath || []).slice();
      });
      return result;
    },

    // 父组件可调用：获取当前表单数据
    getData () {
      return this.getOutputData(this.form);
    },

    // 父组件可调用：获取各平台类目详情（用于提交前组装）
    getCategoryPathDetail () {
      const detail = {};
      for (const platform of PLATFORM_LIST) {
        const ref = this.$refs[`platform-category-select-${platform}`];
        const select = Array.isArray(ref) ? ref[0] : ref;
        detail[platform] = select && select.getCategoryPathDetail
          ? select.getCategoryPathDetail()
          : [];
      }
      return detail;
    },

    // 修改erp分类绑定平台分类（POST /product-service/platformCategoryRel/save）
    // 父组件保存商品时调用此方法，将平台类目绑定关系持久化
    async saveData () {
      // 详情模式下禁止提交（此时无 PlatformCategorySelect ref，数据为空）
      if (this.isDisabled) return;
      const productCategoryId = this.productInfo.productCategoryId;
      if (!productCategoryId) return;
      const detail = this.getCategoryPathDetail();
      const list = [];
      PLATFORM_LIST.forEach(platform => {
        const pathDetail = detail[platform] || [];
        // platformCategoryId 保存从父级到子级的每一层分类 ID，逗号分隔
        const platformCategoryId = pathDetail.map(d => String(d.categoryId)).join(',');
        list.push({
          id: this.platformRelIdMap[platform] || undefined,
          productCategoryId: String(productCategoryId),
          platformId: PLATFORM_CODE_MAP[platform],
          platformCategoryId: platformCategoryId,
        });
      });
      try {
        const res = await this.axios.post(api.save_platformCategoryRel, list);
        if (res.data.code !== 0) {
          this.$Message.error(res.data.message || '保存平台类目绑定关系失败');
        }
      } catch (err) {
        console.error('[savePlatformCategoryRel] 保存失败', err);
        this.$Message.error('保存平台类目绑定关系失败');
      }
    }
  }
};
</script>

<style lang="less" scoped>
.product-platform-category {
  width: 100%;
}
.ppc-view-value {
  color: #17233d;
}
.ppc-form {
  width: 100%;
}
.form-item-flex-wrap {
  display: flex;
  justify-content: space-around;
  .ppc-view-item {
    min-width: 300px;
    line-height: 32px;
    font-size: 14px;
  }
  .ppc-view-label {
    color: #515a6e;
    font-weight: 500;
  }
  .ivu-form-item {
    margin-bottom: 0;
  }
}
</style>
